import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { NotificationService } from 'src/app/shared/lib/notification/notification.service';
import {
  CuentaBancoRef,
  MovimientoIntradayInput,
  RealizarTransferenciaDialogData,
} from '../../models/movimientos-intraday.models';
import { MovimientosIntradayService } from '../../services/movimientos-intraday.service';

@Component({
  selector: 'app-realizar-transferencia',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    MatDialogModule,
    TablerIconsModule,
  ],
  templateUrl: './realizar-transferencia.component.html',
  styleUrl: './realizar-transferencia.component.scss',
})
export class RealizarTransferenciaComponent implements OnInit, OnDestroy {
  newTransferenciaBancaria: MovimientoIntradayInput & {
    id?: string;
    createdAt?: string;
  } = {} as MovimientoIntradayInput;
  myForm: FormGroup;
  loading: boolean = false;
  success: boolean = false;
  ListaBancosPorMoneda: any[] = [];
  ListaBancos: any[] = [];
  needTipoCambio: boolean = false;
  placeHolderMoneda: string = 'S/0.00';
  readonly isCuentaCargoLocked: boolean;

  constructor(
    public dialogRef: MatDialogRef<RealizarTransferenciaComponent>,
    public dialog: MatDialog,
    private movimientosIntradayService: MovimientosIntradayService,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: RealizarTransferenciaDialogData,
    private notificationService: NotificationService
  ) {
    this.myForm = this.fb.group({
      fechaOperacion: new FormControl(new Date(), Validators.required),
      moneda: new FormControl('Soles', Validators.required),
      tipoCambio: [{ value: null, disabled: true }, Validators.required],
      cuentaCargo: new FormControl('', Validators.required),
      cuentaDestino: new FormControl('', Validators.required),
      monto: new FormControl(null, Validators.required),
    });

    this.ListaBancos = data?.ListaBancos ?? [];
    this.isCuentaCargoLocked = Boolean(data?.lockCuentaCargo && data?.cuentaCargo);
    this.ListaBancosPorMoneda = this.ListaBancos.filter(
      (b: any) => b.tipo === 'PEN'
    );

    if (this.data?.cuentaCargo) {
      if (this.data.cuentaCargo.tipo === 'USD') {
        this.myForm.get('moneda')?.setValue('Dólares');
        this.placeHolderMoneda = '$0.00';
        this.ListaBancosPorMoneda = this.ListaBancos.filter(
          (b: any) => b.tipo === 'USD'
        );
      }
      this.myForm.get('cuentaCargo')?.setValue(this.data.cuentaCargo);
      if (this.isCuentaCargoLocked) {
        this.myForm.get('cuentaCargo')?.disable();
      }
    }
  }

  changeMoneda(targetMoneda?: 'Soles' | 'Dólares'): void {
    if (this.isCuentaCargoLocked) return;
    const currentMoneda = this.myForm.get('moneda')?.value;
    const nextMoneda =
      targetMoneda ?? (currentMoneda === 'Soles' ? 'Dólares' : 'Soles');
    if (currentMoneda === nextMoneda) return;

    this.myForm.get('tipoCambio')?.disable();
    this.myForm.get('tipoCambio')?.setValue(null);
    this.needTipoCambio = false;
    this.myForm.get('cuentaCargo')?.setValue('');
    this.myForm.get('moneda')?.setValue(nextMoneda);
  }

  DisableSubmit(): boolean {
    if (
      (this.myForm.get('comisionBancaria')?.value === true &&
        !this.myForm.invalid) ||
      this.myForm.get('OSOC')?.value === true
    ) {
      return false;
    }
    return true;
  }

  ngOnInit(): void {
    this.myForm.get('cuentaCargo')?.valueChanges.subscribe((value) => {
      if (
        value?.tipo &&
        this.myForm.get('cuentaDestino')?.value &&
        value.tipo !== this.myForm.get('cuentaDestino')?.value?.tipo
      ) {
        this.myForm.get('tipoCambio')?.enable();
        this.needTipoCambio = true;
      } else {
        this.myForm.get('tipoCambio')?.disable();
        this.myForm.get('tipoCambio')?.setValue(null);
        this.needTipoCambio = false;
      }
    });

    this.myForm.get('cuentaDestino')?.valueChanges.subscribe((value) => {
      if (
        value?.tipo &&
        this.myForm.get('cuentaCargo')?.value &&
        value.tipo !== this.myForm.get('cuentaCargo')?.value?.tipo
      ) {
        this.myForm.get('tipoCambio')?.enable();
        this.needTipoCambio = true;
      } else {
        this.myForm.get('tipoCambio')?.setValue(null);
        this.myForm.get('tipoCambio')?.disable();
        this.needTipoCambio = false;
      }
    });

    this.myForm.get('moneda')?.valueChanges.subscribe((value) => {
      if (value === 'Soles') {
        this.placeHolderMoneda = 'S/0.00';
        this.ListaBancosPorMoneda = this.ListaBancos.filter(
          (b: any) => b.tipo === 'PEN'
        );
      } else {
        this.placeHolderMoneda = '$0.00';
        this.ListaBancosPorMoneda = this.ListaBancos.filter(
          (b: any) => b.tipo === 'USD'
        );
      }
    });
  }

  onNumberInput(event: any): void {
    const input = event.target.value;
    const numericValue = input
      .replace(/[^0-9.]/g, '')
      .replace(/(\..*?)\..*/g, '$1');

    if (input !== numericValue) {
      this.myForm.get(event.target.name)?.setValue(numericValue);
    }
  }

  getControl(controlName: string) {
    return this.myForm.get(controlName);
  }

  onDateChange(event: any, campo: string): void {
    const inputDate = event.target.value;

    const [day, month, year] = inputDate
      .split('/')
      .map((part: string) => parseInt(part, 10));
    if (!day || !month || !year) {
      console.error('Fecha inválida');
      return;
    }

    const parsedDate = new Date(year, month - 1, day);
    this.myForm.get(campo)?.setValue(parsedDate);
  }

  Submit(): void {
    if (!this.loading) {
      this.loading = true;
      this.TransferenciaBancaria();
    }
  }

  TransferenciaBancaria(): void {
    const formValue = this.myForm.getRawValue();

    this.newTransferenciaBancaria = {
      fechaOperacion: formValue.fechaOperacion?.toISOString(),
      monto: Number(formValue.monto),
      tipoCambio: Number(formValue.tipoCambio),
      cuentaCargo: this.toCuentaBancoRef(formValue.cuentaCargo),
      cuentaDestino: this.toCuentaBancoRef(formValue.cuentaDestino),
      estado: 'CREADO',
    };

    this.movimientosIntradayService
      .create(this.newTransferenciaBancaria)
      .subscribe({
        next: (result: any) => {
          this.success = true;
          this.newTransferenciaBancaria.createdAt = new Date().toISOString();
          this.newTransferenciaBancaria.id =
            result?.createMovimientoIntraday?.id;
        },
        error: (error: any) => {
          console.error('Ocurrió un error:', error);
          this.notificationService.notifyError(
            'Error al realizar transferencia bancaria.'
          );
          this.loading = false;
        },
        complete: () => {
          this.notificationService.showSuccessAlert(
            'Transferencia bancaria realizada exitosamente.',
            () => this.OnClose()
          );
          this.loading = false;
        },
      });
  }

  private toCuentaBancoRef(
    account: RealizarTransferenciaDialogData['cuentaCargo'],
  ): CuentaBancoRef {
    return {
      id: account?.id ?? '',
      nombre: account?.nombre ?? '',
      tipo: account?.tipo as CuentaBancoRef['tipo'],
    };
  }

  onSave(): void {
    this.Submit();
  }

  OnClose(): void {
    this.dialogRef.close({
      success: this.success,
      newTransferenciaBancaria: this.newTransferenciaBancaria,
    });
  }

  onCancel(): void {
    this.OnClose();
  }

  ngOnDestroy(): void {
    this.OnClose();
  }
}
